/**
 * Where a wall's Shift label goes: the highest spot, nearest the wall's visual centre along X, at which
 * the whole text box lies inside the wall's outline. Pure geometry, so it can be checked without a scene.
 * The outline is the convex hull of the wall's mesh, scaled and rolled as the wall is drawn.
 */

export type Pt = [number, number];

const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/** Convex hull (Andrew's monotone chain), counter-clockwise, no repeated points. */
export function convexHull(points: readonly Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const uniq = pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1][0] || p[1] !== pts[i - 1][1]);
  if (uniq.length < 3) return uniq;
  const lower: Pt[] = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

/** The outline as drawn: each point scaled (a negative scale mirrors it), then rolled by `deg` about the origin. */
export function drawnOutline(hull: readonly Pt[], sx: number, sy: number, deg: number): Pt[] {
  const r = (deg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  return convexHull(hull.map(([x, y]): Pt => {
    const px = x * sx, py = y * sy;
    return [px * c - py * s, px * s + py * c];
  }));
}

/** Whether a point is inside a counter-clockwise convex polygon, within `eps`. */
export function insideConvex(poly: readonly Pt[], x: number, y: number, eps = 1e-4): boolean {
  if (poly.length < 3) return false;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    if ((b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]) < -eps) return false;
  }
  return true;
}

/**
 * The centre of a `2·halfW × 2·halfH` text box. X comes first: the outline's centre line, moving off it
 * only when no height on it holds the box. Down that line the box starts as high as it can be and drops
 * until all four corners are inside. A wall too small for the box gets its centre.
 */
export function labelSpot(outline: readonly Pt[], halfW: number, halfH: number, step = 0.01): Pt {
  const xs = outline.map(p => p[0]), ys = outline.map(p => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const fits = (x: number, y: number) =>
    insideConvex(outline, x - halfW, y - halfH) && insideConvex(outline, x + halfW, y - halfH)
    && insideConvex(outline, x - halfW, y + halfH) && insideConvex(outline, x + halfW, y + halfH);
  const reach = (maxX - minX) / 2;
  for (let k = 0; k * step <= reach; k++) {
    for (const x of k === 0 ? [cx] : [cx + k * step, cx - k * step]) {
      for (let y = maxY - halfH; y >= minY + halfH - 1e-9; y -= step) if (fits(x, y)) return [x, y];
    }
  }
  return [cx, (minY + maxY) / 2];
}

/** A roll as the mappers read it: whole degrees, or one decimal for the 7.5° steps. A full turn reads 0, never
 *  360 or -360: a roll the file holds as a full turn or more, either way round, or one that rounds up to one, is
 *  shown inside it. */
export const degrees = (d: number) => {
  const r = Number(d.toFixed(1));
  return `${Math.abs(r) >= 360 ? Number((r % 360).toFixed(1)) : r}°`;
};

/** A wall's side caption; a centre wall, square, triangle and crouch have none (''). */
export function wallSideText(wallType: string): string {
  return wallType === 'wall_left' || wallType === 'angle_left' ? 'Left' : wallType === 'wall_right' || wallType === 'angle_right' ? 'Right' : '';
}

/** How far right of and above the gizmo's centre the roll caption's bottom-left corner sits. */
export const ROLL_LABEL_GAP = 0.04;

/** Where the roll caption's bottom-left corner goes: just inside the quadrant between the move gizmo's
 *  X and Y arrows, which are centred on the wall's drawn centre `(cx, cy)` and point along +X and +Y. */
export function rollLabelSpot(cx: number, cy: number): Pt {
  return [cx + ROLL_LABEL_GAP, cy + ROLL_LABEL_GAP];
}
